import { useEffect } from "react";
import * as THREE from "three/webgpu";
import { MeshBVH } from "three-mesh-bvh";
import {
  type BuildBoundsTreeRequest,
  type BuildBoundsTreeResult,
  type BVHIndexArray,
  buildBoundsTreeAsync,
} from "./bvhWorkerClient.ts";

type SupportedArray = Float32Array | Uint8Array | Uint16Array | Uint32Array;

interface BoundsTreeDeps<G extends object, I, R> {
  capture: (geometry: G) => I | null;
  clear: (geometry: G) => void;
  build: (input: I) => Promise<R>;
  apply: (geometry: G, result: R) => void;
}

interface BoundsTreeState<G extends object, V> {
  geometry: G;
  latestVersion: V | null;
  runningVersion: V | null;
  appliedVersion: V | null;
  queued: boolean;
  canceled: boolean;
}

export class AsyncBoundsTreeManager<G extends object, I, R, V = unknown> {
  private readonly states = new WeakMap<G, BoundsTreeState<G, V>>();
  private readonly queue: BoundsTreeState<G, V>[] = [];
  private running = false;

  constructor(private readonly deps: BoundsTreeDeps<G, I, R>) {}

  request(geometry: G, version: V): void {
    const state = this.getState(geometry);
    state.canceled = false;
    const unchanged =
      state.appliedVersion !== null &&
      Object.is(state.appliedVersion, version) &&
      state.runningVersion === null;
    const alreadyRunning =
      state.runningVersion !== null && Object.is(state.runningVersion, version);
    const alreadyQueued =
      state.queued && Object.is(state.latestVersion, version);
    if (unchanged || alreadyRunning || alreadyQueued) return;

    state.latestVersion = version;
    this.deps.clear(geometry);
    this.enqueue(state);
    this.pump();
  }

  cancel(geometry: G): void {
    const state = this.states.get(geometry);
    if (!state) return;
    state.canceled = true;
    state.latestVersion = null;
    state.runningVersion = null;
    state.appliedVersion = null;
    // Splice out of the queue — previously we only flipped `queued = false`,
    // which left the state (and its strong reference to `geometry`) in the
    // FIFO until the pump drained it. During rapid geometry churn that
    // pinned the geometry plus any captured typed-array build input for
    // every cancelled-but-not-yet-dequeued job.
    if (state.queued) {
      const idx = this.queue.indexOf(state);
      if (idx >= 0) this.queue.splice(idx, 1);
      state.queued = false;
    }
    this.deps.clear(geometry);
  }

  /**
   * Cancel every queued build and discard pending state. The currently
   * running build (if any) finishes naturally — its result is dropped
   * because the per-state version flips to `canceled`. Returns the count
   * of queue slots cleared, useful for memory-pressure reporting.
   */
  cancelAllPending(): number {
    let cleared = 0;
    for (const state of this.queue) {
      state.canceled = true;
      state.queued = false;
      state.latestVersion = null;
      state.runningVersion = null;
      this.deps.clear(state.geometry);
      cleared++;
    }
    this.queue.length = 0;
    return cleared;
  }

  private getState(geometry: G): BoundsTreeState<G, V> {
    let state = this.states.get(geometry);
    if (!state) {
      state = {
        geometry,
        latestVersion: null,
        runningVersion: null,
        appliedVersion: null,
        queued: false,
        canceled: false,
      };
      this.states.set(geometry, state);
    }
    return state;
  }

  private enqueue(state: BoundsTreeState<G, V>): void {
    if (state.queued) return;
    state.queued = true;
    this.queue.push(state);
  }

  private dequeue(): BoundsTreeState<G, V> | null {
    while (this.queue.length > 0) {
      const next = this.queue.shift()!;
      next.queued = false;
      if (next.canceled || next.latestVersion === null) continue;
      if (next.runningVersion !== null) continue;
      if (
        next.appliedVersion !== null &&
        Object.is(next.appliedVersion, next.latestVersion)
      ) {
        continue;
      }
      return next;
    }
    return null;
  }

  private pump(): void {
    if (this.running) return;
    const state = this.dequeue();
    if (!state) return;
    this.running = true;
    void this.run(state);
  }

  private async run(state: BoundsTreeState<G, V>): Promise<void> {
    if (state.latestVersion === null) {
      this.running = false;
      this.pump();
      return;
    }
    const version = state.latestVersion;
    state.runningVersion = version;
    const input = this.deps.capture(state.geometry);

    try {
      if (input) {
        const result = await this.deps.build(input);
        if (
          !state.canceled &&
          state.runningVersion !== null &&
          Object.is(state.runningVersion, version) &&
          Object.is(state.latestVersion, version)
        ) {
          this.deps.apply(state.geometry, result);
          state.appliedVersion = version;
        }
      } else {
        if (!state.canceled) {
          state.appliedVersion = version;
        }
      }
    } finally {
      state.runningVersion = null;
      if (
        !state.canceled &&
        state.latestVersion !== null &&
        (state.appliedVersion === null ||
          !Object.is(state.appliedVersion, state.latestVersion))
      ) {
        this.enqueue(state);
      }
      this.running = false;
      this.pump();
    }
  }
}

function cloneTypedArray<T extends SupportedArray>(src: T): T {
  return src.slice() as T;
}

function cloneIndexArray(src: ArrayLike<number>): BVHIndexArray {
  if (src instanceof Uint32Array) return new Uint32Array(src);
  if (src instanceof Uint16Array) return new Uint16Array(src);
  return new Uint8Array(src);
}

export function captureBoundsTreeBuildInput(
  geometry: THREE.BufferGeometry,
): BuildBoundsTreeRequest | null {
  const positionAttr = geometry.getAttribute("position");
  if (
    !positionAttr ||
    ("isInterleavedBufferAttribute" in positionAttr &&
      positionAttr.isInterleavedBufferAttribute)
  ) {
    return null;
  }
  if (!(positionAttr.array instanceof Float32Array)) return null;
  if (positionAttr.count === 0) return null;

  const indexAttr = geometry.getIndex();
  if (
    indexAttr &&
    "isInterleavedBufferAttribute" in indexAttr &&
    indexAttr.isInterleavedBufferAttribute
  ) {
    return null;
  }
  if (indexAttr && indexAttr.count === 0) return null;

  return {
    position: cloneTypedArray(positionAttr.array),
    index: indexAttr ? cloneIndexArray(indexAttr.array) : null,
    groups: geometry.groups.map((group) => ({
      start: group.start,
      count: group.count,
      materialIndex: group.materialIndex ?? 0,
    })),
  };
}

export function clearBoundsTree(geometry: THREE.BufferGeometry): void {
  geometry.boundsTree = undefined;
}

export function applySerializedBoundsTree(
  geometry: THREE.BufferGeometry,
  result: BuildBoundsTreeResult,
): void {
  const { serialized } = result;
  if (serialized.index) {
    const currentIndex = geometry.getIndex();
    if (
      currentIndex &&
      !(
        "isInterleavedBufferAttribute" in currentIndex &&
        currentIndex.isInterleavedBufferAttribute
      ) &&
      currentIndex.itemSize === 1 &&
      currentIndex.array.constructor === serialized.index.constructor
    ) {
      currentIndex.array = serialized.index;
      currentIndex.needsUpdate = true;
    } else {
      geometry.setIndex(new THREE.BufferAttribute(serialized.index, 1));
    }
  }

  const bvh = MeshBVH.deserialize(serialized as any, geometry, {
    setIndex: false,
  });
  geometry.boundsTree = bvh as typeof geometry.boundsTree;
  geometry.boundingBox = bvh.getBoundingBox(new THREE.Box3());
}

const boundsTreeManager = new AsyncBoundsTreeManager<
  THREE.BufferGeometry,
  BuildBoundsTreeRequest,
  BuildBoundsTreeResult
>({
  capture: captureBoundsTreeBuildInput,
  clear: clearBoundsTree,
  build: buildBoundsTreeAsync,
  apply: applySerializedBoundsTree,
});

export function requestAsyncBoundsTree(
  geometry: THREE.BufferGeometry,
  version: unknown,
): void {
  boundsTreeManager.request(geometry, version);
}

export function cancelAsyncBoundsTree(geometry: THREE.BufferGeometry): void {
  boundsTreeManager.cancel(geometry);
}

/** Cancel every queued BVH build. Returns the number of pending jobs cleared. */
export function cancelAllPendingAsyncBoundsTrees(): number {
  return boundsTreeManager.cancelAllPending();
}

export function useAsyncBoundsTree(
  geometry: THREE.BufferGeometry | null | undefined,
  version: unknown,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled || !geometry) return;
    requestAsyncBoundsTree(geometry, version);
    return () => {
      cancelAsyncBoundsTree(geometry);
    };
  }, [geometry, version, enabled]);
}
